import {
  GEORGIA_CONTEXT_CODE,
  GEORGIA_DISCOUNT_COUPON_ID,
  GEORGIA_DISCOUNT_ENABLED,
  GEORGIA_PLAN_METRICS_VISIBLE,
  GEORGIA_PRODUCT_CODE,
  GEORGIA_REGISTRATION_FEE_OFF_ENABLED,
  GEORGIA_SALES_ENABLED,
  isGeorgiaContextCode,
  isGeorgiaUserCountry
} from './georgia';
import {
  PORTUGAL_CONTEXT_CODE,
  PORTUGAL_DISCOUNT_COUPON_ID,
  PORTUGAL_DISCOUNT_ENABLED,
  PORTUGAL_PLAN_METRICS_VISIBLE,
  PORTUGAL_PRODUCT_CODE,
  PORTUGAL_REGISTRATION_FEE_OFF_ENABLED,
  PORTUGAL_SALES_ENABLED,
  isPortugalContextCode
} from './portugal';

export type CountryMarketConfig = {
  contextCode: string;
  productCode: string;
  countryCode: string;
  countryName: string;
  salesEnabled: boolean;
  discountEnabled: boolean;
  registrationFeeOffEnabled: boolean;
  discountCouponId: string;
  planMetricsVisible: boolean;
  supportCatalogueTitles: string[];
};

export const COUNTRY_MARKETS: CountryMarketConfig[] = [
  {
    contextCode: PORTUGAL_CONTEXT_CODE,
    productCode: PORTUGAL_PRODUCT_CODE,
    countryCode: 'PT',
    countryName: 'Portugal',
    salesEnabled: PORTUGAL_SALES_ENABLED,
    discountEnabled: PORTUGAL_DISCOUNT_ENABLED,
    registrationFeeOffEnabled: PORTUGAL_REGISTRATION_FEE_OFF_ENABLED,
    discountCouponId: PORTUGAL_DISCOUNT_COUPON_ID,
    planMetricsVisible: PORTUGAL_PLAN_METRICS_VISIBLE,
    supportCatalogueTitles: ['Costa del Sol']
  },
  {
    contextCode: GEORGIA_CONTEXT_CODE,
    productCode: GEORGIA_PRODUCT_CODE,
    countryCode: 'GE',
    countryName: 'Georgia',
    salesEnabled: GEORGIA_SALES_ENABLED,
    discountEnabled: GEORGIA_DISCOUNT_ENABLED,
    registrationFeeOffEnabled: GEORGIA_REGISTRATION_FEE_OFF_ENABLED,
    discountCouponId: GEORGIA_DISCOUNT_COUPON_ID,
    planMetricsVisible: GEORGIA_PLAN_METRICS_VISIBLE,
    supportCatalogueTitles: []
  }
];

export function normalizeContextCode(code?: string | null) {
  return (code || '').trim().toUpperCase();
}

export function getCountryMarketByContextCode(code?: string | null) {
  const normalizedCode = normalizeContextCode(code);
  return COUNTRY_MARKETS.find((market) => market.contextCode === normalizedCode) || null;
}

export function isCountryMarketContextCode(code?: string | null) {
  return !!getCountryMarketByContextCode(code);
}

export function getCountryMarketByProductCode(productCode?: string | null) {
  const normalizedProductCode = (productCode || '').trim();
  return COUNTRY_MARKETS.find((market) => market.productCode === normalizedProductCode) || null;
}

export function getCountryMarketByCountryCode(countryCode?: string | null) {
  const normalizedCountryCode = normalizeContextCode(countryCode);
  return COUNTRY_MARKETS.find((market) => market.countryCode === normalizedCountryCode) || null;
}

export function isCountryMarketProduct(product?: { code?: string | null } | null, market?: CountryMarketConfig | null) {
  const code = (product?.code || '').trim();
  const subtype = ((product as any)?.subtype || '').trim().toLowerCase();
  const names = [(product as any)?.name?.es, (product as any)?.name?.en, (product as any)?.name?.pt, (product as any)?.name]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

  const matchingMarkets = market ? [market] : COUNTRY_MARKETS;
  return matchingMarkets.some(
    (item) =>
      code === item.productCode ||
      subtype.includes(item.countryName.toLowerCase()) ||
      names.some((value) => value.includes(item.countryName.toLowerCase()))
  );
}

export function normalizeUserCountry(country?: string | null) {
  const normalizedCountry = normalizeContextCode(country);
  if (normalizedCountry === 'GE' || normalizedCountry === 'GEORGIA') return 'GE';
  if (normalizedCountry === 'PT' || normalizedCountry === 'PORTUGAL') return 'PT';
  if (normalizedCountry === 'ES' || normalizedCountry === 'ESPANA' || normalizedCountry === 'SPAIN') return 'ES';
  return normalizedCountry;
}

export function getAvailableUiLanguages(params: { contextCode?: string | null; userCountry?: string | null } = {}) {
  const languages = new Set(['es', 'en']);
  if (isPortugalContextCode(params.contextCode)) languages.add('pt');
  if (isGeorgiaContextCode(params.contextCode) || isGeorgiaUserCountry(params.userCountry)) {
    languages.add('ka');
    languages.add('ru');
  }
  return Array.from(languages);
}
